import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { dateToString, isDateValid, stringToDate } from '../utils';

interface UseSingleDatePickerParams {
  date?: Date;
  minDate?: Date;
  maxDate?: Date;
  onChange(date?: Date): void;
}

export const useSingleDatePicker = ({
  date,
  minDate,
  maxDate,
  onChange,
}: UseSingleDatePickerParams) => {
  const [internalDate, setInternalDate] = useState(date);

  const [inputValue, setInputValue] = useState(date && dateToString(date));

  const [isOpen, setIsOpen] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);

  const submitRef = useRef<HTMLButtonElement>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInternalDate(date);
  }, [date]);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    // We are about to close ➜ focus the trigger
    // Hack: use a timeout to give focus on next tick
    setTimeout(() => {
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    }, 0);

    setIsOpen(false);
  }, []);

  const handleEsc = useCallback(
    e => {
      if (e.key === 'Escape') {
        close();
      }
    },
    [close],
  );

  const onDateChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (isDateValid({ date: value, minDate, maxDate })) {
        setInternalDate(stringToDate(value));
      }

      setInputValue(value);
    },
    [maxDate, minDate],
  );

  const onInternalChange = useCallback((_date: Date) => {
    setInputValue(dateToString(_date));

    setInternalDate(_date);

    if (submitRef.current) {
      submitRef.current.focus();
    }
  }, []);

  const validateDate = useCallback(() => {
    if (!internalDate || !inputValue) {
      return;
    }

    if (!isDateValid({ date: inputValue, minDate, maxDate })) {
      setInputValue(dateToString(internalDate));
    } else {
      setInternalDate(stringToDate(inputValue));
    }
  }, [inputValue, internalDate, maxDate, minDate]);

  const onInputKeydown = useCallback(
    (evt: KeyboardEvent<HTMLInputElement>) => {
      if (evt.key === 'Enter') {
        validateDate();

        if (submitRef.current) {
          submitRef.current.focus();
        }
      }
    },
    [validateDate],
  );

  const commit = useCallback(() => {
    onChange(internalDate);

    close();
  }, [close, internalDate, onChange]);

  return {
    state: {
      isOpen,
      internalDate,
      inputValue,
      setInternalDate,
      setInputValue,
    },
    refs: {
      inputRef,
      submitRef,
      triggerRef,
    },
    handlers: {
      onInternalChange,
      onDateChange,
      onInputKeydown,
      commit,
      open,
      close,
      handleEsc,
    },
    helpers: {
      validateDate,
    },
  };
};
